import axios, { type AxiosError, type AxiosRequestConfig } from 'axios'
import type { BaseQueryFn } from '@reduxjs/toolkit/query'

/**
 * The one HTTP client, and the one place a token, a refresh and an error shape live.
 *
 * Every surface answers with the same error body, so a failure is normalised here
 * once rather than unpacked at every call site: a screen reads `code` and `message`
 * and never a status number.
 */
export interface ApiErrorShape {
  status: number
  code: string
  message: string
  details?: Record<string, unknown>
}

export interface AxiosBaseQueryArgs {
  url: string
  method?: AxiosRequestConfig['method']
  data?: unknown
  params?: AxiosRequestConfig['params']
  headers?: Record<string, string>
  /**
   * A write that must not happen twice sets this. The API **refuses** a request to
   * an idempotent route without one — a 422 now rather than a double charge later.
   * Left unset, a fresh key is generated per request; pass one to make a retry
   * genuinely the same request.
   */
  idempotencyKey?: string
}

/** In memory only. A refresh token lives in an httpOnly cookie the browser holds
 *  and script cannot read, which is the point of the browser transport. */
let accessToken: string | null = null
export const setAccessToken = (t: string | null) => void (accessToken = t)
export const getAccessToken = () => accessToken

/**
 * The codes that end a session, and what each one means.
 *
 * A 401 is not the whole story. Three of these are **403**, so the desk
 * treated them as ordinary refusals: somebody whose access had just been
 * taken away kept a signed-in-looking screen with every table failing, and
 * nothing said why. 1a says the opposite for the phone — *"the next thing
 * they tap explains that and signs them out"* — and the desk is the surface
 * where access is taken away.
 *
 * `SESSION_IDLE_EXPIRED` is the browser's thirty minutes, which is the one
 * that will happen daily and the one where nothing is wrong.
 */
export const SESSION_ENDED: Record<string, string> = {
  SESSION_IDLE_EXPIRED:
    'You were signed out after a while without use, because this desk can move money and open people’s records. Nothing you were doing has been lost.',
  ADMIN_ACCESS_REMOVED:
    'Your access to this desk has been removed. If that is a surprise, ask whoever manages administrators.',
  ACCOUNT_SUSPENDED: 'This account has been suspended. Please contact support.',
  UNAUTHENTICATED: 'You have been signed out.',
}

let onLapsed: (why: string) => void = () => {}
export const onSessionLapsed = (fn: (why: string) => void) => void (onLapsed = fn)

export const http = axios.create({
  baseURL: import.meta.env.VITE_API_ORIGIN ?? '',
  // The refresh cookie has to travel, and it is the whole session on a reload.
  withCredentials: true,
  headers: { 'x-client': 'browser' },
})

/**
 * The token goes on the **instance**, not on the wrapper.
 *
 * It used to be added inside `axiosBaseQuery` alone, so anything that reached
 * for `http` directly went out unauthenticated. One thing does: `ExportButton`,
 * because a download is a blob rather than a cached query and does not belong
 * in RTK Query. **Every export on the desk answered 401** — Members, Providers,
 * Bookings, Payments and a class's enrolments — and the button said only "That
 * export could not be taken", which is true and useless.
 *
 * On the instance it is true for whatever calls `http` next, which is the
 * property the old arrangement lacked.
 */
http.interceptors.request.use((config) => {
  if (accessToken) config.headers.set('Authorization', `Bearer ${accessToken}`)
  return config
})

const WRITES = new Set(['post', 'put', 'patch', 'delete'])

/**
 * A key for one write, from `crypto.randomUUID` where there is one.
 *
 * `crypto.randomUUID` exists **only in a secure context** — HTTPS, or
 * `localhost`. Served from anything else it is not a function, and since a key
 * is attached to every write, the call threw before the request left the page:
 * every button on the desk failed instantly and each screen reported it as the
 * thing it was doing having been refused. Signing in said the password was
 * wrong about a request that was never sent.
 *
 * Production is HTTPS and development is `localhost`, so this is a fallback
 * rather than the path anybody takes. It exists because the failure it
 * prevents is silent, blames the person, and took a browser to find.
 */
const idempotencyKeyFor = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6]! & 0x0f) | 0x40
  bytes[8] = (bytes[8]! & 0x3f) | 0x80
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export function axiosBaseQuery(): BaseQueryFn<AxiosBaseQueryArgs, unknown, ApiErrorShape> {
  return async ({ url, method = 'GET', data, params, headers, idempotencyKey }) => {
    try {
      const verb = method.toString().toLowerCase()
      const result = await http.request({
        url,
        method,
        data,
        params,
        /* No `Authorization` here — the instance's interceptor adds it, so
           there is one place a request becomes authenticated. */
        headers: {
          ...(WRITES.has(verb)
            ? { 'Idempotency-Key': idempotencyKey ?? idempotencyKeyFor() }
            : {}),
          ...headers,
        },
      })
      return { data: result.data }
    } catch (err) {
      const e = err as AxiosError<{ error?: { code: string; message: string; details?: Record<string, unknown> } }>
      const status = e.response?.status ?? 0

      const code = e.response?.data?.error?.code ?? ''

      /**
       * The session went. Signal it, with the sentence; what to draw is the
       * app's decision, not this layer's.
       *
       * Read from the **code**, not the status. This was `status === 401`,
       * and three of the four ways a desk session ends answer 403 — so being
       * removed from the team, or suspended, left somebody looking at a desk
       * that had stopped working and said nothing about why.
       */
      if (accessToken && code in SESSION_ENDED) onLapsed(SESSION_ENDED[code]!)
      else if (status === 401 && accessToken) onLapsed(SESSION_ENDED.UNAUTHENTICATED!)

      return {
        error: {
          status,
          code: code || (status === 0 ? 'NETWORK' : 'INTERNAL'),
          message:
            e.response?.data?.error?.message ??
            (status === 0 ? 'No connection. Check your network and try again.' : 'Something went wrong.'),
          details: e.response?.data?.error?.details,
        },
      }
    }
  }
}
